import React, { useMemo } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowRightIcon, MapPinIcon, StarIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useSeo } from '../hooks/useSeo';
import { cities, getCity } from '../data/cities';
import { services } from '../data/services';
import { projects } from '../data/projects';
import { testimonials } from '../data/testimonials';
import { company } from '../data/company';
import { PageHero } from '../components/PageHero';
import { BeforeAfterSlider } from '../components/BeforeAfterSlider';
import { CtaBand } from '../components/sections/CtaBand';
import { ContactSection } from '../components/sections/ContactSection';
import { breadcrumbSchema, localBusinessSchema } from '../utils/schema';
export function ServiceAreaPage() {
  const {
    slug
  } = useParams();
  const {
    t,
    b,
    lang
  } = useLanguage();
  const city = getCity(slug);
  const localProjects = useMemo(() => projects.filter(project => project.city.startsWith(city?.name ?? '___')), [city]);
  const localTestimonials = useMemo(() => testimonials.filter(item => item.location.startsWith(city?.name ?? '___')), [city]);
  const schema = useMemo(() => {
    if (!city) return [];
    return [localBusinessSchema(city.name), breadcrumbSchema([{
      name: 'Home',
      path: '/'
    }, {
      name: 'Service Areas',
      path: '/#service-areas'
    }, {
      name: `${city.name}, WA`,
      path: `/service-areas/${city.slug}`
    }])];
  }, [city]);
  useSeo({
    title: city ? `Foundation Repair & Crawl Space Services in ${city.name}, WA | ${company.shortName}` : company.shortName,
    description: city ? `Foundation repair, crawl space encapsulation, basement waterproofing, and insulation in ${city.name}, Washington. Free inspections, licensed and insured. Call ${company.phone}.` : '',
    keywords: city ? [`foundation repair in ${city.name} WA`, `crawl space repair ${city.name}`, `basement waterproofing ${city.name} WA`, 'professional foundation contractors Washington'] : undefined,
    schema
  });
  if (!city) return <Navigate to="/" replace data-id="element-449" data-mp="hevofi6urvnh" />;
  const otherCities = cities.filter(item => item.slug !== city.slug);
  return <>
      <PageHero eyebrow={`${t('areas.localTitle')} ${city.name}, WA`} title={lang === 'es' ? `Reparación de Cimientos y Crawl Space en ${city.name}, WA` : `Foundation Repair & Crawl Space Services in ${city.name}, WA`} description={b(city.blurb)} image={company.workImage} imageAlt={t('about.workAlt')} crumbs={[{
      name: t('nav.home'),
      path: '/'
    }, {
      name: t('nav.areas'),
      path: '/#service-areas'
    }, {
      name: `${city.name}, WA`
    }]} data-id="element-450" data-mp="m7gcs46zpgjm" />

      <section className="bg-white py-16 md:py-20" data-id="element-451" data-mp="36vb5x0oll676">
        <div className="mx-auto max-w-page px-4 sm:px-6 lg:px-8" data-id="element-452" data-mp="36xxmtqxdoy22">
          <div className="flex flex-wrap items-center gap-4" data-id="element-453" data-mp="2zdd6jp04rbho">
            <span className="inline-flex items-center gap-2 rounded-full bg-brand-50 px-4 py-2 text-sm font-bold text-brand-800" data-id="element-454" data-mp="2ulbpzqvxiylu">
              <MapPinIcon className="h-4 w-4" aria-hidden="true" data-id="element-455" data-mp="2pnk012euhmka" />
              {b(city.county)}
            </span>
            <span className="inline-flex items-center gap-2 rounded-full bg-cta/10 px-4 py-2 text-sm font-bold text-cta-dark" data-id="element-456" data-mp="2kpifse5onh78">
              {b(city.responseTime)}
            </span>
            {city.homeBase && <span className="inline-flex items-center gap-2 rounded-full bg-navy px-4 py-2 text-sm font-bold text-white" data-id="element-457" data-mp="3daa1o2na7f4d">
                {t('areas.homeBase')}
              </span>}
          </div>

          <h2 className="mt-12 font-display text-2xl font-bold text-navy md:text-3xl" data-id="element-458" data-mp="1wy38s82yobpn">
            {lang === 'es' ? `Servicios en ${city.name}` : `Services in ${city.name}`}
          </h2>
          <ul className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-id="element-459" data-mp="1s1cvfsijju7x">
            {services.map(service => {
            const Icon = service.icon;
            return <li key={service.slug} data-id="element-460" data-mp="3ll8phvevbrjp">
                  <Link to={`/services/${service.slug}`} className="group flex h-full flex-col rounded-lg border border-navy/10 bg-white p-5 transition-[transform,border-color,box-shadow] duration-200 ease-smooth hover:-translate-y-1 hover:border-brand-700 hover:shadow-md" data-id="element-461" data-mp="36v9svx2yoo6c">
                    <Icon className="h-7 w-7 text-brand-700" aria-hidden="true" data-id="element-462" data-mp="8c2wg4t4tzq9" />
                    <h3 className="mt-4 font-display text-base font-bold leading-snug text-navy" data-id="element-463" data-mp="3pmaocb4vi1l5">
                      {b(service.title)} — {city.name}
                    </h3>
                    <span className="mt-auto inline-flex items-center gap-1.5 pt-4 text-sm font-bold text-brand-700" data-id="element-464" data-mp="2e6lztfbz0ui8">
                      {t('cta.learnMore')}
                      <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-smooth group-hover:translate-x-1" aria-hidden="true" data-id="element-465" data-mp="2j6qh37kpdt9i" />
                    </span>
                  </Link>
                </li>;
          })}
          </ul>
        </div>
      </section>

      {localProjects.length > 0 && <section className="bg-mist py-16 md:py-20" data-id="element-466" data-mp="164sqpf1hx2b6">
          <div className="mx-auto max-w-page px-4 sm:px-6 lg:px-8" data-id="element-467" data-mp="igjbwx2p9etj">
            <h2 className="font-display text-2xl font-bold text-navy md:text-3xl" data-id="element-468" data-mp="di822mcen4bb">
              {t('areas.localProjects')} {city.name}
            </h2>
            <div className="mt-8 grid gap-6 md:grid-cols-2" data-id="element-469" data-mp="rafrft1czszz">
              {localProjects.map(project => <article key={project.id} className="overflow-hidden rounded-lg border border-navy/10 bg-white" data-id="element-470" data-mp="mhmonapgauqj">
                  <BeforeAfterSlider before={project.before} after={project.after} beforeAlt={b(project.beforeAlt)} afterAlt={b(project.afterAlt)} className="aspect-[3/2] w-full" data-id="element-471" data-mp="3uwmuidhpcwk6" />
                  <div className="p-6" data-id="element-472" data-mp="1yti4916bwmbq">
                    <p className="text-xs font-bold uppercase tracking-wider text-brand-700" data-id="element-473" data-mp="23lrukibcvoej">
                      {b(project.label)} • {project.city} • {project.year}
                    </p>
                    <p className="mt-3 text-sm leading-relaxed text-ink-muted" data-id="element-474" data-mp="1ay5o92aqu5nz">{b(project.description)}</p>
                    <Link to={`/services/${project.serviceSlug}`} className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-brand-700 hover:text-brand-800" data-id="element-475" data-mp="1fvxfrv7h7prn">
                      {t('cta.learnMore')}
                      <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-476" data-mp="2wb8rnt9x7th3" />
                    </Link>
                  </div>
                </article>)}
            </div>
          </div>
        </section>}

      {localTestimonials.length > 0 && <section className="bg-white py-16 md:py-20" data-id="element-477" data-mp="posy2xm4n5gv">
          <div className="mx-auto max-w-page px-4 sm:px-6 lg:px-8" data-id="element-478" data-mp="kndg5ef8xf2p">
            <div className="grid gap-6 md:grid-cols-2" data-id="element-479" data-mp="fwwwvdoquz8f">
              {localTestimonials.map(item => <figure key={item.id} className="rounded-lg border border-navy/10 bg-mist p-6" data-id="element-480" data-mp="ay3i7hxq126o">
                  <div className="flex gap-1" role="img" aria-label={t('testimonials.rating')} data-id="element-481" data-mp="5yh9nlsu8a47">
                    {Array.from({
                length: 5
              }).map((_, star) => <StarIcon key={star} className="h-4 w-4 fill-[#C58900] text-[#C58900]" aria-hidden="true" data-id="element-482" data-mp="1z8f4epz487l" data-mpi={star} />)}
                  </div>
                  <blockquote className="mt-4 text-base italic leading-relaxed text-ink-soft" data-id="element-483" data-mp="3o5dpoy0g9sif">
                    “{b(item.quote)}”
                  </blockquote>
                  <figcaption className="mt-4 text-sm font-bold text-navy" data-id="element-484" data-mp="1mcz7ryxwoo20">
                    {item.name} · <span className="font-medium text-ink-muted" data-id="element-485" data-mp="1mb5yebvwrpqb">{item.location}</span>
                  </figcaption>
                </figure>)}
            </div>
          </div>
        </section>}

      <section className="bg-mist py-12" data-id="element-486" data-mp="li3au2awekz0">
        <div className="mx-auto max-w-page px-4 sm:px-6 lg:px-8" data-id="element-487" data-mp="lh20k01pi2ic">
          <h2 className="font-display text-lg font-bold text-navy" data-id="element-488" data-mp="qgejwatdcrg6">{t('areas.allAreas')}</h2>
          <ul className="mt-4 flex flex-wrap gap-2" data-id="element-489" data-mp="chezxggsx0qc">
            {otherCities.map(item => <li key={item.slug} data-id="element-490" data-mp="hfynflb1mkkt">
                <Link to={`/service-areas/${item.slug}`} className="inline-flex rounded-full border border-navy/15 bg-white px-3.5 py-1.5 text-sm font-semibold text-ink-soft transition-colors duration-150 ease-smooth hover:border-brand-700 hover:text-brand-700" data-id="element-491" data-mp="hgzuzfsuooxs">
                  {item.name}, WA
                </Link>
              </li>)}
          </ul>
        </div>
      </section>

      <CtaBand data-id="element-492" data-mp="3me40f8azjbdi" />
      <ContactSection data-id="element-493" data-mp="3mdkt0liyo5dq" />
    </>;
}